import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useVoiceConversation } from "@/hooks/useVoiceConversation";
import { WaveAnimation } from "./WaveAnimation";
import type { VoiceMessage, SIPOCData, QuestionType } from "@acde/shared";

interface VoiceModeProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId?: string;
  onMessage?: (message: VoiceMessage) => void;
  previousMessages?: VoiceMessage[];
  processName?: string;
  sipocData?: SIPOCData;
  currentPhase?: string;
  completionPercentage?: number;
  currentQuestion?: string;
  currentQuestionIndex?: number;
  totalQuestions?: number;
  answeredQuestions?: number;
  allQuestions?: Array<{
    text: string;
    id?: string;
    type?: QuestionType;
    options?: string[];
  }>;
  attachments?: Array<{
    id?: string;
    name: string;
    type: string;
    analysis?: string;
    uploadedAt?: string;
  }>;
  onSubmit?: () => void;
  onFileUpload?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isUploading?: boolean;
  allowFileUpload?: boolean;
}

export function VoiceMode({
  isOpen,
  onClose,
  sessionId,
  onMessage,
  previousMessages,
  processName,
  sipocData,
  currentPhase,
  completionPercentage,
  currentQuestion,
  currentQuestionIndex,
  totalQuestions,
  answeredQuestions,
  allQuestions,
  attachments,
  onSubmit,
  onFileUpload,
  isUploading,
  allowFileUpload,
}: VoiceModeProps) {
  // Track if we're truly continuing (user has participated, not just received a greeting)
  const hasUserParticipated =
    previousMessages?.some(
      (m) =>
        m.role === "user" &&
        m.content?.trim() &&
        m.content.trim() !== "..." &&
        m.content.trim().length > 0
    ) ?? false;
  const isContinuingSession =
    previousMessages && previousMessages.length > 0 && hasUserParticipated;

  const {
    isConnected,
    isConnecting,
    voiceState,
    messages,
    currentTranscript,
    isUserSpeaking,
    error,
    isMuted,
    startConversation,
    endConversation,
    toggleMute,
    retryConnection,
  } = useVoiceConversation({
    sessionId,
    previousMessages,
    processName,
    sipocData,
    currentPhase,
    completionPercentage,
    currentQuestion,
    currentQuestionIndex,
    totalQuestions,
    allQuestions,
    attachments,
  });

  const [mounted, setMounted] = useState(false);
  const [hasAttemptedConnection, setHasAttemptedConnection] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [showTextInput, setShowTextInput] = useState(false);
  const [textInput, setTextInput] = useState("");
  const dialogRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Responsive wave size based on window width
  const [waveSize, setWaveSize] = useState(280);

  useEffect(() => {
    const updateWaveSize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      // Use smaller dimension and cap the size
      const minDim = Math.min(width, height);
      if (minDim < 400) {
        setWaveSize(Math.min(180, minDim * 0.45));
      } else if (minDim < 640) {
        setWaveSize(Math.min(220, minDim * 0.4));
      } else if (minDim < 900) {
        setWaveSize(260);
      } else {
        setWaveSize(300);
      }
    };

    updateWaveSize();
    window.addEventListener("resize", updateWaveSize);
    return () => window.removeEventListener("resize", updateWaveSize);
  }, []);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setHasAttemptedConnection(false);
    }
  }, [isOpen]);

  // Auto-start when opened
  useEffect(() => {
    if (isOpen && !hasAttemptedConnection && !isConnected && !isConnecting) {
      setHasAttemptedConnection(true);
      startConversation();
    }
  }, [
    isOpen,
    hasAttemptedConnection,
    isConnected,
    isConnecting,
    startConversation,
  ]);

  // Track which messages we've already forwarded
  // Initialize to previousMessages length so we don't re-forward known messages
  const forwardedCountRef = useRef(previousMessages?.length ?? 0);

  // Reset forwarded count when previousMessages changes (e.g., loading saved transcript)
  useEffect(() => {
    if (previousMessages) {
      forwardedCountRef.current = previousMessages.length;
    }
  }, [previousMessages]);

  useEffect(() => {
    if (messages.length > forwardedCountRef.current && onMessage) {
      const newMessages = messages.slice(forwardedCountRef.current);
      newMessages.forEach((msg) => onMessage(msg));
      forwardedCountRef.current = messages.length;
    }
  }, [messages, onMessage]);

  const combinedMessages = useMemo(() => {
    const messageMap = new Map<string, VoiceMessage>();
    (previousMessages || []).forEach((msg) => messageMap.set(msg.id, msg));
    messages.forEach((msg) => messageMap.set(msg.id, msg));

    return Array.from(messageMap.values()).sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
  }, [messages, previousMessages]);

  // Auto-scroll transcript
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [combinedMessages, currentTranscript]);

  const handleClose = useCallback(async () => {
    if (isConnected) {
      await endConversation();
    }
    onClose();
  }, [isConnected, endConversation, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (mounted && dialogRef.current) {
      dialogRef.current.focus();
    }
  }, [mounted]);

  // Get the last assistant message for display
  const lastAssistantMessage = useMemo(
    () => [...combinedMessages].reverse().find((m) => m.role === "assistant"),
    [combinedMessages]
  );

  // Filter out empty or placeholder messages for display
  const displayMessages = useMemo(
    () =>
      combinedMessages.filter((m) => {
        const content = m.content?.trim();
        // Filter out empty, placeholder, or very short meaningless messages
        return (
          content && content !== "..." && content !== "…" && content.length > 0
        );
      }),
    [combinedMessages]
  );

  // Render markdown-like bold text
  const renderMessageContent = (content: string) => {
    if (!content) return null;
    // Simple markdown bold parsing
    const parts = content.split(/\*\*(.*?)\*\*/g);
    return parts.map((part, i) =>
      i % 2 === 1 ? (
        <strong key={i} className="font-semibold">
          {part}
        </strong>
      ) : (
        part
      )
    );
  };

  if (!isOpen || !mounted) return null;

  const content = (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Voice interview mode"
      tabIndex={-1}
      className="fixed inset-0 z-50 bg-gradient-to-br from-[#000A14] via-[#001029] to-[#000A14] flex flex-col"
    >
      {/* Ambient background effects - fixed position */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-primary/10 rounded-full blur-3xl animate-pulse" />
        <div
          className="absolute bottom-1/4 right-1/4 w-48 sm:w-80 h-48 sm:h-80 bg-cyan-500/5 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "1s" }}
        />
      </div>

      {/* Header - fixed height */}
      <header className="relative shrink-0 flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 z-30">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-primary to-cyan-500 flex items-center justify-center shadow-lg shadow-primary/20">
            <svg
              className="w-4 h-4 sm:w-5 sm:h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
              />
            </svg>
          </div>
          <div>
            <h2 className="text-white font-semibold text-sm sm:text-base">
              Discovery Interview
            </h2>
            <div className="flex items-center gap-1.5">
              <div
                className={`w-2 h-2 rounded-full ${
                  isConnected
                    ? isMuted
                      ? "bg-red-400"
                      : "bg-green-400"
                    : isConnecting
                    ? "bg-yellow-400 animate-pulse"
                    : "bg-gray-500"
                }`}
              />
              <span className="text-white/50 text-xs sm:text-sm" aria-live="polite" role="status">
                {isConnecting
                  ? isContinuingSession
                    ? "Resuming session..."
                    : "Connecting..."
                  : isConnected
                  ? isMuted
                    ? "Mic paused"
                    : isContinuingSession
                    ? "Continuing"
                    : "Connected"
                  : "Disconnected"}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowTranscript(!showTranscript)}
            className={`p-2.5 rounded-xl transition-all ${
              showTranscript
                ? "bg-primary/30 text-white"
                : "bg-white/10 text-white/60 hover:text-white hover:bg-white/20"
            }`}
            title={showTranscript ? "Hide transcript" : "Show transcript"}
            aria-label={showTranscript ? "Hide transcript" : "Show transcript"}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
              />
            </svg>
          </button>

          <button
            onClick={handleClose}
            className="p-2.5 rounded-xl bg-white/10 text-white/60 hover:text-white hover:bg-white/20 transition-all"
            title="Close"
            aria-label="Close voice mode"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </header>

      {/* Progress Bar - Matches chat mode styling */}
      {totalQuestions && totalQuestions > 0 && (
        <div className="shrink-0 relative z-20 px-4 sm:px-6 py-2 border-b border-white/10">
          <div className="flex items-center gap-2 max-w-xl mx-auto">
            <div
              className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={totalQuestions}
              aria-valuenow={answeredQuestions ?? 0}
              aria-label="Interview progress"
            >
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{
                  width: `${
                    (answeredQuestions ?? 0) >= totalQuestions
                      ? 100
                      : ((currentQuestionIndex ?? 0) / totalQuestions) * 100
                  }%`,
                }}
              />
            </div>
            <span className="text-white/60 text-xs whitespace-nowrap">
              {(answeredQuestions ?? 0) >= totalQuestions
                ? `${totalQuestions}/${totalQuestions} complete`
                : `Q${(currentQuestionIndex ?? 0) + 1} of ${totalQuestions}`}
            </span>
          </div>
        </div>
      )}

      {/* Main content area - flex grow, centered */}
      <main className="flex-1 flex flex-col items-center justify-center relative z-10 overflow-hidden px-4">
        {/* Wave animation - absolutely centered */}
        <div className="shrink-0" aria-hidden="true">
          <WaveAnimation state={voiceState} size={waveSize} />
        </div>

        {/* Message display area - below the wave */}
        <div className="w-full max-w-xl mt-6 sm:mt-8 space-y-3 min-h-[120px]">
          {/* Current live transcript */}
          {currentTranscript ? (
            <div
              className={`p-4 rounded-2xl backdrop-blur-sm border transition-all ${
                isUserSpeaking
                  ? "bg-white/10 border-white/20"
                  : "bg-gradient-to-r from-primary/20 to-cyan-500/10 border-primary/30"
              }`}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                    isUserSpeaking ? "bg-white/20" : "bg-primary/30"
                  }`}
                >
                  {isUserSpeaking ? (
                    <svg
                      className="w-3.5 h-3.5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="w-3.5 h-3.5 text-cyan-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                      />
                    </svg>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-white/40 font-medium uppercase tracking-wider mb-1">
                    {isUserSpeaking ? "You" : "Assistant"}
                  </p>
                  <p className="text-white/90 text-sm sm:text-base leading-relaxed">
                    {currentTranscript}
                  </p>
                </div>
              </div>
            </div>
          ) : lastAssistantMessage ? (
            /* Show last message when idle */
            <div className="p-4 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm">
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                  <svg
                    className="w-3.5 h-3.5 text-cyan-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                    />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-white/30 font-medium uppercase tracking-wider mb-1">
                    Last response
                  </p>
                  <p className="text-white/50 text-sm leading-relaxed line-clamp-3">
                    {lastAssistantMessage.content}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            /* Empty state */
            <div className="text-center py-4">
              <p className="text-white/30 text-sm">
                {isConnecting
                  ? isContinuingSession
                    ? `Resuming from question ${
                        (currentQuestionIndex ?? 0) + 1
                      }...`
                    : "Preparing your interview..."
                  : "Start speaking when ready"}
              </p>
            </div>
          )}
        </div>

        {/* Error message */}
        {error && (
          <div role="alert" className="mt-4 px-4 py-3 bg-red-500/20 border border-red-500/30 rounded-xl text-red-300 text-sm max-w-md text-center">
            <div className="flex items-center gap-2 justify-center">
              <svg
                className="w-4 h-4 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <span>{error}</span>
            </div>
          </div>
        )}
      </main>

      {/* Controls - fixed at bottom */}
      <footer className="relative shrink-0 z-30">
        {/* Text Input Bar (when enabled) */}
        {showTextInput && isConnected && (
          <div className="px-4 pb-2 animate-fade-in">
            <div className="flex items-center gap-2 bg-white/10 rounded-2xl p-2">
              <input
                ref={textInputRef}
                type="text"
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && textInput.trim()) {
                    // Send as a user message (simulated - in production would use the AI)
                    if (onMessage) {
                      onMessage({
                        id: crypto.randomUUID(),
                        role: "user",
                        content: textInput.trim(),
                        timestamp: new Date(),
                        isFinal: true,
                      });
                    }
                    setTextInput("");
                  }
                }}
                placeholder="Type a message..."
                aria-label="Type a message"
                className="flex-1 px-4 py-2 bg-transparent text-white placeholder-white/40 focus:outline-none text-sm"
              />
              <button
                onClick={() => {
                  if (textInput.trim() && onMessage) {
                    onMessage({
                      id: crypto.randomUUID(),
                      role: "user",
                      content: textInput.trim(),
                      timestamp: new Date(),
                      isFinal: true,
                    });
                    setTextInput("");
                  }
                }}
                disabled={!textInput.trim()}
                className="p-2.5 rounded-xl bg-primary text-white disabled:opacity-40 transition-all"
                title="Send message"
                aria-label="Send message"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Hidden file input */}
        {allowFileUpload && onFileUpload && (
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={onFileUpload}
            aria-label="Upload file"
          />
        )}

        {/* Control Buttons */}
        <div className="flex items-center justify-center gap-3 sm:gap-4 px-4 py-4 pb-14 sm:pb-12">
          {/* File upload button */}
          {allowFileUpload && onFileUpload && (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={!isConnected || isUploading}
              className="p-3.5 sm:p-4 rounded-full transition-all shadow-lg bg-white/10 text-white/70 hover:text-white hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed"
              title="Attach file"
              aria-label="Attach file"
            >
              {isUploading ? (
                <div className="w-5 h-5 sm:w-6 sm:h-6 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                  />
                </svg>
              )}
            </button>
          )}

          {/* Keyboard/Text toggle */}
          <button
            onClick={() => {
              setShowTextInput(!showTextInput);
              if (!showTextInput) {
                setTimeout(() => textInputRef.current?.focus(), 100);
              }
            }}
            disabled={!isConnected}
            className={`p-3.5 sm:p-4 rounded-full transition-all shadow-lg ${
              showTextInput
                ? "bg-primary/40 text-primary shadow-primary/20"
                : "bg-white/10 text-white/70 hover:text-white hover:bg-white/20"
            } disabled:opacity-40 disabled:cursor-not-allowed`}
            title="Type message"
            aria-label={showTextInput ? "Hide text input" : "Show text input"}
          >
            <svg
              className="w-5 h-5 sm:w-6 sm:h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>

          {/* Microphone control with enhanced visual feedback */}
          <div className="relative">
            {/* Subtle glow ring when actively listening (not muted and connected) */}
            {isConnected && !isMuted && voiceState === "listening" && (
              <div
                className="absolute inset-[-3px] rounded-full bg-gradient-to-r from-green-500/40 to-emerald-500/40 animate-[breathe_3s_ease-in-out_infinite]"
                style={{ filter: "blur(6px)" }}
              />
            )}
            <button
              onClick={toggleMute}
              disabled={!isConnected}
              className={`relative p-3.5 sm:p-4 rounded-full transition-all shadow-lg ${
                isMuted
                  ? "bg-red-500/30 text-red-400 hover:bg-red-500/40 shadow-red-500/20"
                  : isConnected && voiceState === "listening"
                  ? "bg-green-500/30 text-green-400 hover:bg-green-500/40 shadow-green-500/20 ring-1 ring-green-500/50"
                  : "bg-white/10 text-white/70 hover:text-white hover:bg-white/20"
              } disabled:opacity-40 disabled:cursor-not-allowed`}
              title={
                isMuted ? "Resume listening (unmute)" : "Pause listening (mute)"
              }
              aria-label={isMuted ? "Resume listening" : "Pause listening"}
            >
              {isMuted ? (
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"
                  />
                </svg>
              ) : (
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                  />
                </svg>
              )}
            </button>
            {/* Status label with subtle dot indicator */}
            {isConnected && (
              <div
                className={`absolute -bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-1 text-[10px] font-medium whitespace-nowrap ${
                  isMuted ? "text-red-400" : "text-green-400"
                }`}
              >
                {!isMuted && (
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-[breathe_3s_ease-in-out_infinite]" />
                )}
                {isMuted ? "Paused" : "Listening"}
              </div>
            )}
          </div>

          {/* Show Submit button when interview is complete */}
          {totalQuestions &&
          answeredQuestions === totalQuestions &&
          onSubmit ? (
            <button
              onClick={() => {
                handleClose();
                onSubmit();
              }}
              className="px-6 sm:px-8 py-3 sm:py-3.5 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold transition-all shadow-lg shadow-green-500/25 flex items-center gap-2 text-sm sm:text-base"
            >
              <svg
                className="w-4 h-4 sm:w-5 sm:h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="hidden sm:inline">Submit Interview</span>
              <span className="sm:hidden">Submit</span>
            </button>
          ) : (
            <button
              onClick={handleClose}
              className="px-6 sm:px-8 py-3 sm:py-3.5 rounded-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold transition-all shadow-lg shadow-red-500/25 flex items-center gap-2 text-sm sm:text-base"
            >
              <svg
                className="w-4 h-4 sm:w-5 sm:h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z"
                />
              </svg>
              <span className="hidden sm:inline">End Interview</span>
              <span className="sm:hidden">End</span>
            </button>
          )}

          {!isConnected && !isConnecting && error && (
            <button
              onClick={() => {
                setHasAttemptedConnection(false);
                retryConnection();
              }}
              className="px-5 sm:px-6 py-3 sm:py-3.5 rounded-full bg-gradient-to-r from-primary to-cyan-500 hover:from-primary-hover hover:to-cyan-600 text-white font-semibold transition-all shadow-lg shadow-primary/25 flex items-center gap-2 text-sm sm:text-base"
            >
              <svg
                className="w-4 h-4 sm:w-5 sm:h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Retry
            </button>
          )}
        </div>
      </footer>

      {/* Transcript sidebar overlay */}
      {showTranscript && (
        <>
          {/* Mobile: full screen overlay */}
          <div className="fixed inset-0 sm:hidden bg-black/95 backdrop-blur-lg z-40 flex flex-col animate-fade-in">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div>
                <h3 className="text-white font-semibold">Conversation</h3>
                <p className="text-white/40 text-xs">
                  {displayMessages.length} messages
                </p>
              </div>
              <button
                onClick={() => setShowTranscript(false)}
                className="p-2.5 rounded-xl bg-white/10 text-white/70 hover:text-white hover:bg-white/20 transition-all"
                title="Close transcript"
                aria-label="Close transcript"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {displayMessages.length === 0 ? (
                <p className="text-white/30 text-sm text-center py-8">
                  Conversation will appear here...
                </p>
              ) : (
                displayMessages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${
                      message.role === "user" ? "justify-end" : "justify-start"
                    }`}
                  >
                    <div
                      className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm ${
                        message.role === "user"
                          ? "bg-white/20 text-white"
                          : "bg-primary/30 text-white/90"
                      }`}
                    >
                      <p className="whitespace-pre-wrap leading-relaxed">
                        {renderMessageContent(message.content)}
                      </p>
                      <span className="text-xs text-white/30 mt-1 block">
                        {new Date(message.timestamp).toLocaleTimeString(
                          "en-NZ",
                          { hour: "2-digit", minute: "2-digit" }
                        )}
                      </span>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Desktop: side panel */}
          <div className="hidden sm:flex fixed right-0 top-0 bottom-0 w-80 bg-black/60 backdrop-blur-xl border-l border-white/10 flex-col z-40 animate-fade-in">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div>
                <h3 className="text-white font-semibold">Conversation</h3>
                <p className="text-white/40 text-xs">
                  {displayMessages.length} messages
                </p>
              </div>
              <button
                onClick={() => setShowTranscript(false)}
                className="p-2 rounded-lg bg-white/10 text-white/60 hover:text-white hover:bg-white/20 transition-all"
                title="Close transcript"
                aria-label="Close transcript"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {displayMessages.length === 0 ? (
                <p className="text-white/30 text-sm text-center py-8">
                  Conversation will appear here...
                </p>
              ) : (
                displayMessages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${
                      message.role === "user" ? "justify-end" : "justify-start"
                    }`}
                  >
                    <div
                      className={`max-w-[90%] px-3.5 py-2 rounded-2xl text-sm ${
                        message.role === "user"
                          ? "bg-white/20 text-white"
                          : "bg-primary/30 text-white/90"
                      }`}
                    >
                      <p className="whitespace-pre-wrap leading-relaxed">
                        {renderMessageContent(message.content)}
                      </p>
                      <span className="text-xs text-white/30 mt-1 block">
                        {new Date(message.timestamp).toLocaleTimeString(
                          "en-NZ",
                          { hour: "2-digit", minute: "2-digit" }
                        )}
                      </span>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>
        </>
      )}
    </div>
  );

  return createPortal(content, document.body);
}
